import { useAiChatActions } from '@euler/app/flows/ai-suite/chat/AiChatActions';
import { isNotFoundHttpError } from '@euler/lib/request/HttpError';
import { BiAnswerAction } from '@euler/model/bi/answer';
import { BiChangeSetDetail } from '@euler/model/bi/change-set';
import { useServiceFactory } from '@euler/services/factory';
import { useLocalObservable } from '@euler/utils/hooks/useLocalObservable';
import {
  EMPTY,
  catchError,
  distinctUntilChanged,
  filter,
  from,
  map,
  merge,
  scan,
  skip,
  startWith,
  switchMap,
} from 'rxjs';
import { useBiDashboardHost } from '../BiDashboardHostContext';

export type BiChangeSetState =
  | { kind: 'loading' }
  | { kind: 'ready'; detail: BiChangeSetDetail }
  /** Gone, or another member's (change sets are their proposer's only). */
  | { kind: 'missing' }
  | { kind: 'failed' };

/**
 * A change set and its diff, read again when a tap on it settles and when
 * the dashboard the chat is open on moves on (which may leave it stale).
 * A failed read after the first keeps what is on screen.
 */
export function useBiChangeSet(changeSetId: string): BiChangeSetState {
  const { biService } = useServiceFactory();
  const { tapSettled$ } = useAiChatActions();
  const host = useBiDashboardHost();
  return useLocalObservable<BiChangeSetState>(
    () => {
      const settled$ = tapSettled$.pipe(
        filter(
          tap =>
            tap.kind === 'bi' &&
            (tap.answer.action === BiAnswerAction.ApplyChanges ||
              tap.answer.action === BiAnswerAction.DiscardChanges) &&
            tap.answer.changeSetId === changeSetId,
        ),
      );
      const moved$ = host
        ? host.revision$.pipe(distinctUntilChanged(), skip(1))
        : EMPTY;
      return merge(settled$, moved$).pipe(
        startWith(null),
        switchMap(() =>
          from(biService.changeSet(changeSetId)).pipe(
            map((detail): BiChangeSetState => ({ kind: 'ready', detail })),
            catchError((error): [BiChangeSetState] => {
              console.error('BI change set failed', changeSetId, error);
              return [
                isNotFoundHttpError(error)
                  ? { kind: 'missing' }
                  : { kind: 'failed' },
              ];
            }),
          ),
        ),
        scan((current: BiChangeSetState, next: BiChangeSetState) =>
          current.kind === 'ready' && next.kind !== 'ready' ? current : next,
        ),
      );
    },
    { kind: 'loading' },
    [biService, changeSetId, tapSettled$, host],
  );
}
